import React, { useState, useEffect } from 'react';
import {
  X,
  Download,
  Edit3,
  Save,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  FileAudio,
  MessageSquare,
  Languages,
  Mic,
  Volume2,
  Eye,
  EyeOff
} from 'lucide-react';
import { 
  translationApi, 
  TranslationJob, 
  TranslationStep 
} from '../../services/audioApi';
import { formatDistanceToNow } from 'date-fns';
import LocalizedTextArea from '../Common/LocalizedTextArea';

interface TranslationJobDetailProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
  onJobUpdate: (job: TranslationJob) => void;
}

const TranslationJobDetail: React.FC<TranslationJobDetailProps> = ({
  jobId,
  isOpen,
  onClose,
  onJobUpdate
}) => {
  const [job, setJob] = useState<TranslationJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingStep, setEditingStep] = useState<string | null>(null);
  const [editedText, setEditedText] = useState<string>('');
  //const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [executingStep, setExecutingStep] = useState<string | null>(null);
  const [showOriginalAudio, setShowOriginalAudio] = useState(false);
  const [showTranslatedAudio, setShowTranslatedAudio] = useState(false);

  useEffect(() => {
    if (isOpen && jobId) {
      fetchJobDetails();
    }
  }, [isOpen, jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await translationApi.getJobDetails(jobId);
      setJob(response.data);
      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch job details:', error);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStep = (step: TranslationStep) => {
    if (!step.canEdit) return;
    
    setEditingStep(step.id);
    // Set initial text based on step type
    if (step.name.includes('Speech-to-Text')) {
      setEditedText(step.data?.transcribedText || '');
    } else if (step.name.includes('Translation')) {
      setEditedText(step.data?.translatedText || '');
    }
  };

  const handleSaveEdit = async (stepId: string) => {
    if (!job || !editedText.trim()) return;

    try {
      const step = job.steps.find(s => s.id === stepId);
      if (!step) return;

      const updateData: any = {};
      if (step.name.includes('Speech-to-Text')) {
        updateData.transcribedText = editedText;
      } else if (step.name.includes('Translation')) {
        updateData.translatedText = editedText;
      }

      await translationApi.updateStepData(job.jobId, stepId, updateData);
      
      // Refresh job details
      await fetchJobDetails();
      
      setEditingStep(null);
      setEditedText('');
    } catch (error: any) {
      console.error('Failed to update step:', error);
      setError('Failed to update step data');
    }
  };

  const handleExecuteStep = async (stepId: string) => {
    if (!job) return;

    try {
      setExecutingStep(stepId);
      await translationApi.executeStep(job.jobId, stepId);
      
      // Refresh job details
      await fetchJobDetails();
      
      // Notify parent component
      if (job) {
        onJobUpdate(job);
      }
    } catch (error: any) {
      console.error('Failed to execute step:', error);
      setError('Failed to execute step');
    } finally {
      setExecutingStep(null);
    }
  };

  const handleRetryFromStep = async (stepId: string) => {
    if (!job) return;

    try {
      setExecutingStep(stepId);
      await translationApi.retryFromStep(job.jobId, stepId);
      
      // Refresh job details
      await fetchJobDetails();
      
      // Notify parent component
      if (job) {
        onJobUpdate(job);
      }
    } catch (error: any) {
      console.error('Failed to retry from step:', error);
      setError('Failed to retry from step');
    } finally {
      setExecutingStep(null);
    }
  };

  const getStepIcon = (step: TranslationStep) => {
    switch (step.name) {
      case 'Audio Upload':
        return <FileAudio className="w-5 h-5" />;
      case 'Speech-to-Text':
        return <Mic className="w-5 h-5" />;
      case 'Text Translation':
        return <Languages className="w-5 h-5" />;
      case 'Text-to-Speech':
        return <Volume2 className="w-5 h-5" />;
      default:
        return <MessageSquare className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'processing':
        return 'text-blue-600 bg-blue-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'processing':
        return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Translation Job Details
            </h2>
            {job && (
              <p className="text-sm text-gray-600 mt-1">
                Job #{job.jobId.slice(-8)} • Created {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading job details...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchJobDetails}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : job ? (
            <div className="space-y-6">
              {/* Job Overview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Job Overview</h3>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                    {getStatusIcon(job.status)}
                    <span className="ml-2 capitalize">{job.status}</span>
                    {job.progress > 0 && job.status === 'processing' && (
                      <span className="ml-1">({job.progress}%)</span>
                    )}
                  </div>
                </div>
                
                {job.result && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Source Language:</span>
                      <p className="font-medium">{job.result.sourceLanguage.toUpperCase()}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Target Language:</span>
                      <p className="font-medium">{job.result.targetLanguage.toUpperCase()}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Voice:</span>
                      <p className="font-medium">{job.result.voiceName}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Processing Time:</span>
                      <p className="font-medium">{(job.result.processingTime / 1000).toFixed(1)}s</p>
                    </div>
                  </div>
                )}

                {job.error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{job.error}</p>
                  </div>
                )}
              </div>

              {/* Audio Files */}
              {job.result && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Audio Files</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Original Audio */}
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Original Audio</h4>
                        <button
                          onClick={() => setShowOriginalAudio(!showOriginalAudio)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {showOriginalAudio ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {showOriginalAudio && job.steps[0]?.data?.originalAudioUrl && (
                        <div className="space-y-2">
                          <audio controls className="w-full">
                            <source src={job.steps[0].data.originalAudioUrl} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                          <div className="text-xs text-gray-500">
                            Duration: {job.result.duration.original}s • 
                            Size: {(job.result.metadata.originalFileSize / 1024 / 1024).toFixed(2)}MB
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Translated Audio */}
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Translated Audio</h4>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setShowTranslatedAudio(!showTranslatedAudio)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {showTranslatedAudio ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <a
                            href={job.result.outputAudioUrl}
                            download
                            className="p-1 bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                      {showTranslatedAudio && (
                        <div className="space-y-2">
                          <audio controls className="w-full">
                            <source src={job.result.outputAudioUrl} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                          <div className="text-xs text-gray-500">
                            Duration: {job.result.duration.translated}s • 
                            Size: {(job.result.metadata.outputFileSize / 1024 / 1024).toFixed(2)}MB
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Steps */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Translation Steps</h3>
                <div className="space-y-4">
                  {job.steps.map((step, index) => (
                    <div key={step.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className={`p-2 rounded-lg ${getStatusColor(step.status)}`}>
                            {getStepIcon(step)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-medium text-gray-900">
                                {index + 1}. {step.name}
                              </h4>
                              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(step.status)}`}>
                                {getStatusIcon(step.status)}
                                <span className="ml-1 capitalize">{step.status}</span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{step.description}</p>

                            {/* Step Data */}
                            {step.data && (
                              <div className="space-y-3">
                                {/* Transcribed Text */}
                                {step.data.transcribedText && (
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <label className="text-sm font-medium text-gray-700">
                                        Transcribed Text
                                      </label>
                                      {step.canEdit && (
                                        <button
                                          onClick={() => handleEditStep(step)}
                                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                                        >
                                          <Edit3 className="w-3 h-3 mr-1" />
                                          Edit
                                        </button>
                                      )}
                                    </div>
                                    {editingStep === step.id ? (
                                      <div className="space-y-2">
                                        <LocalizedTextArea
                                          language={step.data.transcriptionLanguage}
                                          value={editedText}
                                          onChange={(e) => setEditedText(e.target.value)}
                                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          rows={4}
                                        />
                                        <div className="flex items-center space-x-2">
                                          <button
                                            onClick={() => handleSaveEdit(step.id)}
                                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center"
                                          >
                                            <Save className="w-3 h-3 mr-1" />
                                            Save
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingStep(null);
                                              setEditedText('');
                                            }}
                                            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 transition-colors"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        className="p-3 bg-gray-50 rounded border text-sm"
                                        dir={step.data.transcriptionLanguage === 'ur' || step.data.transcriptionLanguage === 'ar' ? 'rtl' : 'auto'}
                                      >
                                        {step.data.transcribedText}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Translated Text */}
                                {step.data.translatedText && (
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <label className="text-sm font-medium text-gray-700">
                                        Translated Text
                                      </label>
                                      {step.canEdit && (
                                        <button
                                          onClick={() => handleEditStep(step)}
                                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                                        >
                                          <Edit3 className="w-3 h-3 mr-1" />
                                          Edit
                                        </button>
                                      )}
                                    </div>
                                    {editingStep === step.id ? (
                                      <div className="space-y-2">
                                        <LocalizedTextArea
                                          language={step.data.targetLanguage}
                                          value={editedText}
                                          onChange={(e) => setEditedText(e.target.value)}
                                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          rows={4}
                                        />
                                        <div className="flex items-center space-x-2">
                                          <button
                                            onClick={() => handleSaveEdit(step.id)}
                                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center"
                                          >
                                            <Save className="w-3 h-3 mr-1" />
                                            Save
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingStep(null);
                                              setEditedText('');
                                            }}
                                            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 transition-colors"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        className="p-3 bg-gray-50 rounded border text-sm"
                                        dir={step.data.targetLanguage === 'ur' || step.data.targetLanguage === 'ar' ? 'rtl' : 'auto'}
                                      >
                                        {step.data.translatedText}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Step Metadata */}
                                <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                                  {step.data.transcriptionLanguage && (
                                    <div>
                                      <span className="font-medium">Language:</span> {step.data.transcriptionLanguage}
                                    </div>
                                  )}
                                  {step.data.transcriptionConfidence && (
                                    <div>
                                      <span className="font-medium">Confidence:</span> {(step.data.transcriptionConfidence * 100).toFixed(1)}%
                                    </div>
                                  )}
                                  {step.data.voiceName && (
                                    <div>
                                      <span className="font-medium">Voice:</span> {step.data.voiceName}
                                    </div>
                                  )}
                                  {step.duration && (
                                    <div>
                                      <span className="font-medium">Duration:</span> {(step.duration / 1000).toFixed(1)}s
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {step.error && (
                              <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                                {step.error}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Step Actions */}
                        <div className="flex items-center space-x-2 ml-4">
                          {step.canRerun && (
                            <button
                              onClick={() => handleExecuteStep(step.id)}
                              disabled={executingStep === step.id}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                              title="Re-run step"
                            >
                              {executingStep === step.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {step.status === 'failed' && (
                            <button
                              onClick={() => handleRetryFromStep(step.id)}
                              disabled={executingStep === step.id}
                              className="p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50"
                              title="Retry from this step"
                            >
                              {executingStep === step.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {job && (
              <span>
                Last updated: {formatDistanceToNow(new Date(job.updatedAt), { addSuffix: true })}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchJobDetails}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslationJobDetail;